import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@nykaa/ui-components';
import InlinePriceReveal from '@nykaa/sitewide-prices-revealed/inlinePriceReveal';
import { InlinePRData } from '@nykaa/sitewide-prices-revealed/inlinePriceReveal/types';
import { deviceTypes } from '@nykaa/prices-revealed-shared/constants';
import {
  AddToDynamicWishlist,
  HandleAddToDynamicWishlistParams,
  ParamsForSnackBarCallback,
} from '@nykaa/prices-revealed-shared/types';
import { pushData } from '@nykaa/data-layer';
import { isValidObject } from '@nykaa/utils/validations';
import { SaleConfigTypes } from '@nykaa/product-card-shared/types';
import { State } from 'store/types';
import Snackbar from 'components/Snackbar';
import { redirectAuthPage } from 'shared/helpers/auth';
import { useIsLoggedIn } from 'hooks/auth';

interface Props {
  currentSkuId: string;
  itemsInDynamicWishlist: string[];
  prData: InlinePRData;
  saleConfig: SaleConfigTypes;
  fetchDynamicWishlist: ({ bucket }: { bucket: string }) => Promise<void>;
  addToDynamicWishlist: AddToDynamicWishlist;
  isDifferential: boolean;
}

const Root = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.spacing40};
  max-width: 366px;
`;

const deviceType = deviceTypes.D_SITE;

function View({
  itemsInDynamicWishlist,
  currentSkuId,
  prData,
  saleConfig,
  fetchDynamicWishlist,
  addToDynamicWishlist,
  isDifferential,
}: Props) {
  const isLoggedIn = useIsLoggedIn();
  const config = useSelector(
    (state: State) => state.remoteConfig.configs.pdPriceReveal
  );
  const { isActive } = config || {};
  const [snackbarData, setSnackbarData] = useState<ParamsForSnackBarCallback>({
    show: false,
    isError: false,
    message: '',
  });
  const { isError, message, show: showSnackbar } = snackbarData;

  useEffect(() => {
    const data = {
      isPdpDifferentialPricing: false,
      isPdpPriceRevealPresent: false,
    };
    if (isActive && prData) {
      if (isDifferential) {
        data.isPdpDifferentialPricing = true;
      } else data.isPdpPriceRevealPresent = true;
    }
    pushData(data);
  }, [isActive, prData, isDifferential]);

  const handleAddToDynamicWishlist = (
    data: HandleAddToDynamicWishlistParams
  ) => {
    const { bucket, productId, onSuccess, onError } = data;
    addToDynamicWishlist({
      bucket,
      productId,
      deviceType,
    })
      .then(() => onSuccess())
      .catch(() => onError());
  };

  const handleSnackbarClose = () => {
    setSnackbarData({
      isError: false,
      message: '',
      show: false,
    });
  };

  if (!isActive || !isValidObject(prData)) return null;

  return (
    <Root>
      <InlinePriceReveal
        itemsInDynamicWishlist={itemsInDynamicWishlist}
        currentSkuId={currentSkuId}
        config={config}
        fetchDynamicWishlist={fetchDynamicWishlist}
        onAddToDynamicWishlistClick={handleAddToDynamicWishlist}
        redirectAuthPage={redirectAuthPage}
        setSnackbar={setSnackbarData}
        isLoggedIn={isLoggedIn}
        prData={prData}
        saleConfig={isDifferential ? prData : saleConfig}
        isDifferential={isDifferential}
      />
      {showSnackbar && (
        <Snackbar
          autoClose
          closeCallback={handleSnackbarClose}
          content={message}
          snackBarId="prices-revealed"
          errorType={isError}
        />
      )}
    </Root>
  );
}

export default View;
